import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { useSocket } from "./SocketContext";
import { SOCKET_EVENTS } from "../socket/socketEvents";
import ConversationApi from "../../../api/ConversationApi";
import Auth from "../../../utils/AuthUtils";
import { toast } from "react-toastify";

const ChatContext = createContext(null);

export const ChatProvider = ({ children }) => {
  const notificationSoundRef = useRef(null); //

  useEffect(() => {
    notificationSoundRef.current = new Audio(
      process.env.PUBLIC_URL + "/notification.mp3",
    );
    notificationSoundRef.current.preload = "auto";
    notificationSoundRef.current.volume = 1;

    const enableSound = () => {
      notificationSoundRef.current
        .play()
        .then(() => {
          notificationSoundRef.current.pause();
          notificationSoundRef.current.currentTime = 0;
        })
        .catch(() => {});
      window.removeEventListener("click", enableSound);
    };

    window.addEventListener("click", enableSound);
  }, []);

  // 🔔 Ask browser permission once
  useEffect(() => {
    if ("Notification" in window && Notification.permission !== "granted") {
      Notification.requestPermission();
    }
  }, []);
  const socket = useSocket();

  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [typingUsers, setTypingUsers] = useState({});

  const activeConversationRef = useRef(null);
  const [currentUserId, setCurrentUserId] = useState(null);

  useEffect(() => {
    const user = Auth.getUserDetails();
    if (user) {
      setCurrentUserId(user._id || user.id);
    }
  }, []);
  useEffect(() => {
    activeConversationRef.current = activeConversation;
  }, [activeConversation]);

  /* ================= FETCH CONVERSATIONS ================= */
  const forceRefresh = useCallback(async () => {
    try {
      const res = await ConversationApi.getConversations();
      setConversations(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error("Conversation refresh failed", err);
    }
  }, []);

  /* ================= JOIN CHAT ================= */
 const joinConversation = async (conv) => {
  if (!socket || !conv?._id) return;

    setMessages([]);
    setReplyTo(null);
    setActiveConversation(conv);

    setConversations((prev) =>
      prev.map((c) =>
        String(c._id) === String(conv._id) ? { ...c, unreadCount: 0 } : c,
      ),
    );

    socket.emit(SOCKET_EVENTS.JOIN_CONVERSATION, {
      conversationId: conv._id,
    });

    try {
      const res = await ConversationApi.getMessages(conv._id);

      const msgs = res?.data || [];
      setMessages(msgs);

      // 🔥 NOW MARK THEM AS SEEN
      const unseenIds = msgs
        .filter((m) => !m.seenBy?.includes(currentUserId))
        .map((m) => m._id);

      if (unseenIds.length > 0) {
        socket.emit(SOCKET_EVENTS.MESSAGE_SEEN, {
          conversationId: conv._id,
          messageIds: unseenIds,
        });
      }
    } catch (e) {
      console.error("Fetch messages failed", e);
    }
  };

  const openConversationByLead = async (leadId) => {
    try {
      const res = await ConversationApi.getConversationByLead(leadId);

      const conv = res?.data;

      if (!conv?._id) return;

      await joinConversation(conv);
    } catch (err) {
      console.error("Lead conversation open failed", err);
      toast.error("Conversation not found");
    }
  };

  /* ================= SOCKET LISTENERS ================= */
  useEffect(() => {
    if (!socket) return;

    socket.on(SOCKET_EVENTS.NEW_MESSAGE, ({ conversationId, message }) => {
      const isActiveChat =
        String(activeConversationRef.current?._id) === String(conversationId);

      const senderId =
        typeof message.senderId === "object"
          ? message.senderId?._id
          : message.senderId;

      const isMe = String(senderId) === String(currentUserId);

      // Always refresh conversation list

      // If current chat open → append message
      if (isActiveChat) {
        setMessages((prev) => [...prev, message]);
      }

      if (!isMe) {
        setConversations((prev) =>
          prev.map((c) =>
            String(c._id) === String(conversationId)
              ? {
                  ...c,
                  lastMessage: message, // ✅ ADD THIS
                  unreadCount: isActiveChat
                    ? 0
                    : isMe
                      ? c.unreadCount
                      : (c.unreadCount || 0) + 1,
                }
              : c,
          ),
        );
      }

      // Do not notify for own message
      if (isMe) return;

      // 🔊 Play notification sound
      try {
        if (notificationSoundRef.current) {
          notificationSoundRef.current.currentTime = 0;
          notificationSoundRef.current
            .play()
            .catch((err) => console.log("Audio blocked:", err));
        }
      } catch (err) {
        console.log("Sound error:", err);
      }

      // 🔔 Show toast if different chat
      if (!isActiveChat) {
        toast.info(`${message.senderId?.name || "Someone"} sent a message`);
      }

      // 🖥 Show browser popup if tab hidden
      if (document.hidden && Notification.permission === "granted") {
        new Notification(message.senderId?.name || "New Message", {
          body:
            message.type === "text" ? message.text : `Sent a ${message.type}`,
          icon: "/logo.png",
        });
      }
    });

    socket.on(
      SOCKET_EVENTS.MESSAGE_SEEN,
      ({ conversationId, messageIds, userId }) => {
        if (
          String(activeConversationRef.current?._id) !== String(conversationId)
        )
          return;

        setMessages((prev) =>
          prev.map((m) =>
            messageIds.includes(m._id)
              ? {
                  ...m,
                  seenBy: [...(m.seenBy || []), userId],
                }
              : m,
          ),
        );
      },
    );

    socket.on("messageDelivered", ({ conversationId, messageIds, userId }) => {
      // Agar current chat open nahi hai toh ignore
      if (String(activeConversationRef.current?._id) !== String(conversationId))
        return;

      setMessages((prev) =>
        prev.map((m) =>
          messageIds.includes(m._id)
            ? {
                ...m,
                deliveredTo: [...(m.deliveredTo || []), userId],
              }
            : m,
        ),
      );
    });

    socket.on(SOCKET_EVENTS.MESSAGE_EDITED, ({ conversationId, message }) => {
      console.log("✏️ FRONTEND: MESSAGE_EDITED", message);

      if (String(activeConversationRef.current?._id) !== String(conversationId))
        return;

      setMessages((prev) =>
        prev.map((m) =>
          String(m._id) === String(message._id) ? { ...m, ...message } : m,
        ),
      );
    });

    socket.on(SOCKET_EVENTS.MESSAGE_DELETED, ({ messageId }) => {
      setMessages((prev) =>
        prev.map((m) => (m._id === messageId ? { ...m, isDeleted: true } : m)),
      );
    });

    socket.on(SOCKET_EVENTS.TYPING, ({ conversationId, userId }) => {
      if (String(activeConversationRef.current?._id) !== String(conversationId))
        return;

      if (String(userId) === String(currentUserId)) return;

      setTypingUsers((prev) => ({
        ...prev,
        [userId]: true,
      }));
    });

    socket.on(SOCKET_EVENTS.STOP_TYPING, ({ conversationId, userId }) => {
      if (activeConversationRef.current?._id === conversationId) {
        setTypingUsers((p) => {
          const copy = { ...p };
          delete copy[userId];
          return copy;
        });
      }
    });

    return () => {
      socket.off(SOCKET_EVENTS.MESSAGE_SEEN);
      socket.off(SOCKET_EVENTS.NEW_MESSAGE);
      socket.off(SOCKET_EVENTS.MESSAGE_EDITED);
      socket.off(SOCKET_EVENTS.MESSAGE_DELETED);
      socket.off(SOCKET_EVENTS.TYPING);
      socket.off(SOCKET_EVENTS.STOP_TYPING);
    };
  }, [socket, forceRefresh, currentUserId]);

  useEffect(() => {
    forceRefresh();
  }, [forceRefresh]);

  return (
    <ChatContext.Provider
      value={{
        conversations,
        activeConversation,
        messages,
        currentUserId,
        replyTo,
        setReplyTo,
        typingUsers,

        joinConversation,
        openConversationByLead,

        sendMessage: (payload) =>
          socket.emit(SOCKET_EVENTS.SEND_MESSAGE, {
            conversationId: activeConversation?._id,
            ...payload,
          }),

        editMessage: (messageId, newText) => {
          console.log("🚀 EMIT MESSAGE_EDIT", {
            messageId,
            conversationId: activeConversation?._id,
            newText,
          });

          socket.emit(SOCKET_EVENTS.MESSAGE_EDIT, {
            messageId,
            conversationId: activeConversation?._id,
            newText,
          });
        },

        deleteMessage: (messageId) =>
          socket.emit(SOCKET_EVENTS.MESSAGE_DELETE, {
            messageId,
            conversationId: activeConversation?._id,
          }),

        addReaction: (messageId, emoji) =>
          socket.emit(SOCKET_EVENTS.MESSAGE_REACTION, {
            messageId,
            conversationId: activeConversation?._id,
            emoji,
          }),

        startTyping: (conversationId) =>
          socket.emit(SOCKET_EVENTS.TYPING, { conversationId }),

        stopTyping: (conversationId) =>
          socket.emit(SOCKET_EVENTS.STOP_TYPING, { conversationId }),

        createGroup: (data) => socket.emit(SOCKET_EVENTS.GROUP_CREATE, data),

        addMembers: (conversationId, members) =>
          socket.emit(SOCKET_EVENTS.GROUP_ADD_MEMBERS, {
            conversationId,
            members,
          }),

        removeMember: (conversationId, userId) =>
          socket.emit(SOCKET_EVENTS.GROUP_REMOVE_MEMBER, {
            conversationId,
            userId,
          }),

        leaveGroup: (conversationId) =>
          socket.emit(SOCKET_EVENTS.GROUP_LEAVE, { conversationId }),

        renameGroup: (conversationId, name) =>
          socket.emit(SOCKET_EVENTS.GROUP_RENAME, {
            conversationId,
            name,
          }),

        transferOwnership: (conversationId, newAdminId) =>
          socket.emit(SOCKET_EVENTS.GROUP_TRANSFER_OWNERSHIP, {
            conversationId,
            newAdminId,
          }),
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChat must be used inside ChatProvider");
  }
  return context;
};
